/**
 * `/query/v2` claim marks, placed by OFFSET on the Markdown source.
 *
 * v2 claims carry UTF-16 offsets into the Markdown `answer` the server rendered from the
 * writer's segments, so `answer.slice(start_utf16, end_utf16)` is the claim's source text,
 * emphasis markers included. Wrapping that slice in a tag BEFORE parsing would break the
 * Markdown (a mark opening inside `**`, or inside a table row's pipes). So the mark is
 * applied AFTER parsing: micromark records each mdast text node's source offsets, and a
 * text node is split where a claim starts or ends. Its offsets index the same JavaScript
 * string, so they are UTF-16 units too, and an emoji before a claim shifts nothing.
 *
 * Still the security surface of `AnswerMarkdown`: the marks are mdast nodes with a hast
 * `span` name, never raw HTML, and the sanitiser's allowlist sees them like any other
 * element.
 */
import type { Link, Parent, Root, RootContent, Text } from "mdast";

import type { V2Mark } from "@/lib/queryV2.ts";

export interface V2MarkLabel {
  /** Hover text on the claim's citation chip(s): the verdict and its details. */
  title: string;
  /** The verdict in words, read to screen readers after the chips (never shown). */
  flag: string;
}

export interface V2MarkOptions {
  marks: ReadonlyArray<V2Mark>;
  label: (mark: V2Mark) => V2MarkLabel;
}

type Node = RootContent;

function markNode(mark: V2Mark, title: string, child: Node): Node {
  return {
    type: "v2Mark",
    data: {
      hName: "span",
      hProperties: {
        className: ["v2-mark", `v2-${mark.verdict}`],
        title,
        dataVerdict: mark.verdict,
      },
    },
    children: [child],
  } as unknown as Node;
}

function flagNode(classes: string[], text: string, title?: string): Node {
  return {
    type: "v2Flag",
    data: { hName: "span", hProperties: { className: classes, ...(title ? { title } : {}) } },
    children: [{ type: "text", value: text }],
  } as unknown as Node;
}

/** The citation chip carries the verdict: its colour, plus the details on hover. */
function chipNode(index: number, verdict: string, tooltip: string): Link {
  return {
    type: "link",
    url: `#ev-${index}`,
    // The `a` renderer in `AnswerMarkdown` reads the title as `chip-v2:<verdict>|<tooltip>`.
    title: `chip-v2:${verdict}|${tooltip}`,
    children: [{ type: "text", value: String(index) }],
  };
}

export function remarkV2Marks(options?: V2MarkOptions) {
  return (tree: Root) => {
    if (!options || options.marks.length === 0) return;
    const { marks, label } = options;
    const labels = marks.map((mark) => label(mark));
    // For each mark, the LAST piece of text it covers and the chain of parents above it:
    // the chips and the screen-reader verdict go right after the claim's final word.
    const last = new Map<number, { piece: Node; chain: Parent[] }>();

    const split = (node: Node, chain: Parent[]): Node[] | null => {
      if (node.type !== "text" && node.type !== "inlineCode") return null;
      const position = node.position;
      const s = position?.start.offset;
      const e = position?.end.offset;
      if (s === undefined || e === undefined) return null;
      const hits = marks
        .map((mark, index) => ({ mark, index }))
        .filter(({ mark }) => mark.start < e && s < mark.end);
      if (hits.length === 0) return null;

      // A node whose value is not its source slice (an escape, an entity, a code span's
      // backticks) cannot be cut by offset. It takes the mark that covers most of it,
      // whole: a slightly wide mark, never a missing one.
      if (node.type === "inlineCode" || node.value.length !== e - s) {
        const best = hits.reduce((a, b) =>
          Math.min(e, b.mark.end) - Math.max(s, b.mark.start) >
          Math.min(e, a.mark.end) - Math.max(s, a.mark.start)
            ? b
            : a,
        );
        const wrapped = markNode(best.mark, labels[best.index]?.title ?? "", node);
        last.set(best.index, { piece: wrapped, chain });
        return [wrapped];
      }

      const out: Node[] = [];
      let cursor = s;
      for (const { mark, index } of hits) {
        const a = Math.max(s, mark.start);
        const b = Math.min(e, mark.end);
        if (a > cursor) out.push({ type: "text", value: node.value.slice(cursor - s, a - s) });
        const piece = markNode(mark, labels[index]?.title ?? "", {
          type: "text",
          value: node.value.slice(a - s, b - s),
        } satisfies Text);
        out.push(piece);
        last.set(index, { piece, chain });
        cursor = b;
      }
      if (cursor < e) out.push({ type: "text", value: node.value.slice(cursor - s) });
      return out;
    };

    const walk = (parent: Parent, chain: Parent[]): void => {
      const here = [...chain, parent];
      for (let i = 0; i < parent.children.length; i += 1) {
        const child = parent.children[i] as Node;
        const replacement = split(child, here);
        if (replacement) {
          parent.children.splice(i, 1, ...(replacement as Parent["children"]));
          i += replacement.length - 1;
        } else if ("children" in child) {
          walk(child as Parent, here);
        }
      }
    };
    walk(tree, []);

    for (const [index, { piece, chain }] of last) {
      const mark = marks[index];
      const text = labels[index];
      if (!mark || !text) continue;
      // One coloured chip per source; the verdict's details are the chip's tooltip. A claim
      // with no source still gets a chip-sized "?" so it never looks like plain framing.
      const badge: Node[] = mark.indexes.map((n) => chipNode(n, mark.verdict, text.title));
      if (mark.indexes.length === 0 && mark.verdict !== "supported") {
        badge.push(flagNode(["chip", "chip-v2", `chip-v2-${mark.verdict}`], "?", text.title));
      }
      // Not colour alone: the verdict in words for screen readers.
      badge.push(flagNode(["sr-only"], ` (${text.flag})`));
      // Never inside a link: a chip is itself a link, and a link in a link is invalid.
      // After the outermost link around the claim's last word, otherwise after the word.
      const linkAt = chain.findIndex((node) => node.type === "link");
      const [holder, after] =
        linkAt > 0 ? [chain[linkAt - 1], chain[linkAt] as Node] : [chain[chain.length - 1], piece];
      if (!holder) continue;
      const at = holder.children.indexOf(after as Parent["children"][number]);
      if (at >= 0) holder.children.splice(at + 1, 0, ...(badge as Parent["children"]));
    }
  };
}
